import type { ReactElement } from 'react';
import type { IntlShape } from 'react-intl';
import { enqueueSnackbar } from '~/utils/notifications';

/**
 * One tool a surface can arm: its name, icon, key, state and the gate that
 * decides whether it can be armed now. The toolbar and the radial menu render
 * the same entries, so a tool reads the same and is refused for the same
 * reason wherever it is picked.
 */
export interface ToolEntry {
    /** Stable key; the toolbar button's test id defaults to `tool-<id>`. */
    id: string;
    label: string;
    /** A usage hint shown under the label while the tool is available. */
    hint?: string;
    icon: ReactElement;
    /** The single key that arms the tool, lower case; absent when it has none. */
    hotkey?: string;
    selected: boolean;
    /**
     * Picked while the open operation previews or writes: the tool waits off
     * the views and arms as the operation closes.
     */
    held?: boolean;
    disabled: boolean;
    /** Why the tool cannot be armed now; always set while it is disabled. */
    disabledReason?: string;
    /**
     * Arms the tool. Arming the armed tool leaves it as it is. A disabled
     * entry is not armed: a warning names the tool and why.
     */
    arm: () => void;
    /** What a click on the tool's button does: arms the tool, or, for a tool that can be disarmed, returns the armed or held tool to pan. */
    toggle: () => void;
}

/** The fields of an entry that decide and explain a refusal. */
type RefusalFields = Pick<
    ToolEntry,
    'label' | 'hotkey' | 'disabled' | 'disabledReason'
>;

/** The key as the user sees it on a keycap: `b` reads `B`. */
export function hotkeyLabel(hotkey: string): string {
    return hotkey.toUpperCase();
}

/** The entry's name with its key, as a tooltip names it: `Static Brush (B)`. */
export function labelWithHotkey(
    entry: Pick<ToolEntry, 'label' | 'hotkey'>
): string {
    return entry.hotkey
        ? `${entry.label} (${hotkeyLabel(entry.hotkey)})`
        : entry.label;
}

/**
 * The {@link ToolEntry.disabledReason} for an entry: none while it is
 * enabled, else `reason`, or that the tool is not available now.
 */
export function reasonWhileDisabled(
    intl: IntlShape,
    disabled: boolean,
    reason: string | undefined
): string | undefined {
    if (!disabled) {
        return undefined;
    }
    return reason ?? intl.formatMessage({ id: 'tool.disabled.unavailable' });
}

/**
 * While `entry` is disabled, warns that the tool is refused, naming it, its
 * key and why, and returns true. Returns false for an enabled entry.
 */
export function refused(entry: RefusalFields): boolean {
    if (entry.disabled) {
        enqueueSnackbar(`${labelWithHotkey(entry)}: ${entry.disabledReason}`, {
            variant: 'warning',
        });
        return true;
    }
    return false;
}
